import React, { ForwardedRef, useImperativeHandle, useMemo, useRef } from 'react';
import classNames from 'classnames';
import Chart from '..';
import { ChartRef, MixedChartProps } from '../_config/_interface';
import { useDblIntl} from '@/pages/datalab/utils/utils';
import { useMixedChartsOptions, useChartYaxisRange} from '../_config/_hooks';
import TextBubbles from '@home/components/TextBubbles';
import {setDataLabel, setXLabelAlign} from "@customComp/Chart/chartConfigs/plugs";
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";
import {changeLegendLocation} from "@customComp/Chart/chartConfigs/legend/legendUtils";
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import { isEmpty } from 'lodash-es';
import { resolveSeriesColorsFromLegendColors } from '../chartConfigs/legend/legendUtils';

const MixedChartInner: React.ForwardRefRenderFunction<ChartRef, MixedChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const { chartData, metricNames, chartConfig, fieldInfos } = props;
    
  const { dblFormatMessage } = useDblIntl("chart");
  const chartRef = useRef<ChartRef>(null);
  // 获取实例
  const instance = () => chartRef?.current?.instance();
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance
  }));
  const {
    legends,
    option,
  } = useMixedChartsOptions({
    chartData,
    metricNames,
    fieldInfos,
    chartRef: chartRef?.current?.cRef,
    chartConfig,
  });
  
  let newOption = useMemo(() => {
    // 设置数据标签
    const displayConfig: Record<string, {accuracy: number; unit: string;}> = {};
    metricNames.forEach((metricName: string) => {
      const {accuracy, unit} = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {accuracy: 0, unit: 'bmk'};
      displayConfig[metricName?.replace(/(t[0-2]_)/, '')] = {accuracy, unit};
    });
    let newOption = setDataLabel(option, chartConfig, {metricFormatter: (param, isPercent) => {
      const {accuracy, unit} = displayConfig[param?.seriesName] || {accuracy: 0, unit: 'bmk'};
      return formatDisplayValue({
        value: param.value,
        isPercent,
        accuracy,
        unit,
      });
    },});
    newOption = setXLabelAlign(newOption, {type: chartConfig.xLabel});
    // 设置图例位置
    changeLegendLocation({option, position: chartConfig?.legendPosition});
    if (!isEmpty(chartConfig?.legendColors) && Array.isArray((newOption as any)?.legend?.data)) {
      const legendData = newOption?.legend?.data || [];
      newOption.color = resolveSeriesColorsFromLegendColors({
        legendData,
        legendColors: chartConfig.legendColors,
        palette: LIGHT_COLOR_ARR,
      });
    }
    return newOption;
  }, [option, chartConfig]);

  const rangeOption = useChartYaxisRange({option: newOption, index: '0'});
  const rangeOption2 = useChartYaxisRange({option: newOption, index: '1'});
  const rangeOptionBoth = useChartYaxisRange({option: newOption});

  // y轴range调整
  // 这里旧的组合图中 Y轴范围开关是单独的字段，所以需要兼容旧的逻辑，新的组合图中 Y轴范围开关是和主次轴一起的
  const primaryYAxisRangeSwitch = chartConfig?.yAxisRangeSwitch ?? chartConfig?.primaryYAxis?.yAxisRangeSwitch ;
  const secondaryYAxisRangeSwitch = chartConfig?.yAxisRangeSwitch2 ?? chartConfig?.secondYAxis?.yAxisRangeSwitch;
  if(primaryYAxisRangeSwitch && secondaryYAxisRangeSwitch){
    newOption = rangeOptionBoth;
  }else if(primaryYAxisRangeSwitch){
    newOption = rangeOption;
  }else if(secondaryYAxisRangeSwitch){
    newOption = rangeOption2;
  };

  const leftYAxisName = chartData?.y_axis?.[0]?.name;
  const rightYAxisName = chartData?.y_axis?.[1]?.name;
  const hasYAxisName: boolean =
    Boolean(leftYAxisName) || Boolean(leftYAxisName);
  
  
  return (
    <div className="analysis-chart-container">
      <div
        className={classNames('dbl-charts-top-yAxis-text', {
          'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
        })}
      >
        <div className="dbh-text-exceeds-bubble-left">
          <TextBubbles
            text={leftYAxisName}
          />
        </div>
        <div className="dbh-text-exceeds-bubble-right">
          <TextBubbles
            text={rightYAxisName}
          />
        </div>
      </div>
      <div className="analysis-chart-content">
        <Chart ref={chartRef} option={newOption} notMerge={true}/>
        {legends.length > 1
          ? legends.map((_, index) => (
            <div
              className="legend-title"
              style={{bottom: 20 * (legends.length - index - 1)}}
              key={index}
            >{`${dblFormatMessage('compare')}${index + 1}`}</div>
          ))
          : null}
      </div>
    </div>
  );
};

const MixedChart = React.forwardRef(MixedChartInner);

export default MixedChart;